import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { msg, fmtFecha, TIPOS_EVAL, FirmaPad } from './comun';
import { imprimirCertificado } from './certificadoPrint';

const APTITUD = { APTO: 'Apto', OBSERVACION: 'Apto en observación', LIMITACIONES: 'Apto con limitaciones', NO_APTO: 'No apto' };

/* Paso 4 de la evaluación: certificado de aptitud (SNS-MSP/Form.HCU081/2025).
   Lo emite el profesional que atendió; cualquier usuario del módulo puede ver e imprimir el ya emitido. */
export default function Certificado({ id, estado, onCambio }) {
  const [c, setC] = useState(null);
  const [err, setErr] = useState('');
  const [medioProf, setMedioProf] = useState('DIGITAL');
  const [medioTrab, setMedioTrab] = useState('PAPEL');
  const [firmaTrab, setFirmaTrab] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [anular, setAnular] = useState(false);
  const [motivo, setMotivo] = useState('');

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get(`/ocupacional/evaluaciones/${id}/certificado`);
      setC(data); setErr('');
      if (data.firma_digital && !data.firma_digital.tiene) setMedioProf('PAPEL');
    } catch (e) { setErr(msg(e)); }
  }, [id]);
  useEffect(() => { cargar(); }, [cargar, estado]);

  async function emitir() {
    setGuardando(true); setErr('');
    try {
      await api.post(`/ocupacional/evaluaciones/${id}/certificado`, { medio_prof: medioProf, medio_trab: medioTrab, ...(medioTrab === 'DIGITAL' ? { firma_trab: firmaTrab } : {}) });
      setFirmaTrab(null); await cargar(); if (onCambio) onCambio();
    } catch (e) { setErr(msg(e)); } finally { setGuardando(false); }
  }
  async function anularCert() {
    try {
      await api.post(`/ocupacional/certificados/${c.vigente.COD}/anular`, { motivo });
      setAnular(false); setMotivo(''); await cargar(); if (onCambio) onCambio();
    } catch (e) { setErr(msg(e)); }
  }

  if (err && !c) return <div className="mo-cuerpo"><div className="mo-alert">{err}</div></div>;
  if (!c) return null;
  const v = c.vigente;
  const vp = c.vista_previa;
  const fd = c.firma_digital || { tiene: false };

  return (
    <div className="mo-cuerpo">
      <div className="form-label">Certificado de evaluación médica ocupacional</div>
      {err && <div className="mo-alert">{err}</div>}

      {v && (
        <>
          <div className="mo-ok" style={{ margin: 0 }}>
            Certificado <b>{v.NUMERO}</b> emitido el {fmtFecha(v.FECHA_EMISION)} · {APTITUD[v.CONTENIDO.c && v.CONTENIDO.c.aptitud] || (v.CONTENIDO.b && v.CONTENIDO.b.tipo === 'RETIRO' ? 'Evaluación de retiro' : '—')}.
            {' '}<button className="btn btn-sm btn-primary" onClick={() => imprimirCertificado(v)}>🖨 Imprimir 2 originales / PDF</button>
            {c.es_profesional && <> <button className="btn btn-sm" onClick={() => { setAnular(true); setMotivo(''); }}>Anular certificado</button></>}
          </div>
          <div className="mo-sub" style={{ marginTop: 6 }}>
            Firma del profesional: {v.MEDIO_PROF === 'PAPEL' ? 'en el impreso' : 'digital'} · Firma del trabajador: {v.MEDIO_TRAB === 'PAPEL' ? 'en el impreso' : 'digital'}.
            Se imprimen dos originales: uno para el archivo y otro para el trabajador.
          </div>
          <div className="mo-hash">Huella SHA-256: {v.HASH_CONTENIDO}</div>
          {anular && (
            <div className="mo-panel">
              <span>Anular el certificado. Queda registrado y la evaluación vuelve a «FEMO completo» para poder corregir y emitir otro.</span>
              <input className="form-control" style={{ maxWidth: 280 }} placeholder="Motivo (mínimo 5 letras)" value={motivo} onChange={e => setMotivo(e.target.value)} />
              <button className="btn btn-sm btn-primary" disabled={motivo.trim().length < 5} onClick={anularCert}>Anular</button>
              <button className="btn btn-sm" onClick={() => { setAnular(false); setMotivo(''); }}>Cancelar</button>
            </div>
          )}
        </>
      )}

      {!v && c.puede_emitir && vp && (
        <>
          <div className="mo-note">Revise lo que dirá el certificado. Se arma con el FEMO completo; no incluye datos clínicos, solo aptitud y recomendaciones.</div>
          <div className="mo-dato" style={{ margin: '8px 0' }}>
            <b>{vp.a.apellido1} {vp.a.apellido2} {vp.a.nombre1} {vp.a.nombre2}</b> · {TIPOS_EVAL[vp.b.tipo] || vp.b.tipo}
            {vp.c.aptitud && <> · Aptitud: <b>{APTITUD[vp.c.aptitud] || vp.c.aptitud}</b></>}
            {vp.c.detalle && <div className="mo-sub">Detalle: {vp.c.detalle}</div>}
            {vp.d.descripcion && <div className="mo-sub">Recomendaciones: {vp.d.descripcion}</div>}
            {vp.d.observacion && <div className="mo-sub">Observación: {vp.d.observacion}</div>}
          </div>
          <div className="mo-sub" style={{ marginBottom: 8 }}>{vp.texto}</div>

          <div className="form-label">Su firma ({vp.e.nombre}{vp.e.codigo_medico ? ` · cód. ${vp.e.codigo_medico}` : ''})</div>
          <div className="mo-lin">
            <label className="mo-chk"><input type="radio" name="mprof" disabled={!fd.tiene} checked={medioProf === 'DIGITAL'} onChange={() => setMedioProf('DIGITAL')} /> Firma digital (la registrada en Configuración)</label>
            <label className="mo-chk"><input type="radio" name="mprof" checked={medioProf === 'PAPEL'} onChange={() => setMedioProf('PAPEL')} /> Firmaré en el impreso</label>
          </div>
          {!fd.tiene && fd.motivo && <div className="mo-sub">{fd.motivo}</div>}

          <div className="form-label" style={{ marginTop: 10 }}>Firma del trabajador</div>
          <div className="mo-lin">
            <label className="mo-chk"><input type="radio" name="mtrab" checked={medioTrab === 'PAPEL'} onChange={() => setMedioTrab('PAPEL')} /> Firmará en el impreso</label>
            <label className="mo-chk"><input type="radio" name="mtrab" checked={medioTrab === 'DIGITAL'} onChange={() => setMedioTrab('DIGITAL')} /> Firma digital ahora</label>
          </div>
          {medioTrab === 'DIGITAL' && <FirmaPad onChange={setFirmaTrab} />}

          <div style={{ marginTop: 10 }}>
            <button className="btn btn-primary" disabled={guardando || (medioTrab === 'DIGITAL' && !firmaTrab) || (medioProf === 'DIGITAL' && !fd.tiene)} onClick={emitir}>
              {guardando ? 'Emitiendo…' : '✓ Emitir certificado'}
            </button>
          </div>
        </>
      )}

      {!v && !c.puede_emitir && c.estado === 'FEMO_LISTO' && (
        <div className="mo-note">El FEMO está completo. El certificado lo emite el profesional que atendió, con su propio usuario.</div>
      )}

      {c.anulados && c.anulados.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="form-label">Certificados anulados</div>
          {c.anulados.map(a => <div key={a.COD} className="mo-sub">{a.NUMERO} del {fmtFecha(a.FECHA_EMISION)}, anulado: {a.MOTIVO_ANULACION}</div>)}
        </div>
      )}
    </div>
  );
}
